import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { Promotion, PromotionChannelKind, PromotionInput } from "@/types/sms";
import { PROMOTIONS_QUERY_KEY } from "./usePromotions";

const CHANNEL_KINDS: PromotionChannelKind[] = ["phone", "in_store", "online"];

/**
 * Create/update/archive/unarchive a promotion and its per-channel rows.
 * `createPromotion` inserts the promotion row, then inserts one channel row
 * per kind; `updatePromotion` updates the promotion row then upserts channels
 * on (promotion_id, channel) so an edit never duplicates a channel row.
 */
export const usePromotionMutations = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: PROMOTIONS_QUERY_KEY });

  const createPromotion = useMutation({
    mutationFn: async (input: PromotionInput): Promise<Promotion> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { channels, ...promotionFields } = input;
      const { data: promotion, error } = await sb
        .from("sms_promotions")
        .insert(promotionFields)
        .select()
        .single();
      if (error) throw error;

      const channelRows = CHANNEL_KINDS.map((kind) => ({
        promotion_id: promotion.id,
        channel: kind,
        ...channels[kind],
      }));
      const { error: channelError } = await sb.from("sms_promotion_channels").insert(channelRows);
      if (channelError) throw channelError;

      return { ...promotion, channels: channelRows } as unknown as Promotion;
    },
    onSuccess: invalidate,
  });

  const updatePromotion = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: PromotionInput }): Promise<void> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { channels, ...promotionFields } = input;
      const { error } = await sb.from("sms_promotions").update(promotionFields).eq("id", id);
      if (error) throw error;

      const channelRows = CHANNEL_KINDS.map((kind) => ({
        promotion_id: id,
        channel: kind,
        ...channels[kind],
      }));
      const { error: channelError } = await sb
        .from("sms_promotion_channels")
        .upsert(channelRows, { onConflict: "promotion_id,channel" });
      if (channelError) throw channelError;
    },
    onSuccess: invalidate,
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "active" | "archived" }): Promise<void> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;
      const { error } = await sb.from("sms_promotions").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    createPromotion: createPromotion.mutateAsync,
    isCreating: createPromotion.isPending,
    updatePromotion: (id: string, input: PromotionInput) => updatePromotion.mutateAsync({ id, input }),
    isUpdating: updatePromotion.isPending,
    archivePromotion: (id: string) => setStatus.mutateAsync({ id, status: "archived" }),
    unarchivePromotion: (id: string) => setStatus.mutateAsync({ id, status: "active" }),
  };
};
